/**
 * Landing Page
 *
 * Public marketing page for ProgramRow.
 *
 * This page is reviewed by compliance vetters (A2P 10DLC campaign registration,
 * payment processors) who need to see, without logging in: who operates the
 * service, how to contact them, what the product does, and how the SMS program
 * works. Keep the contact section, the legal-entity line, and the SMS
 * disclosure intact and consistent with /privacy and /terms.
 *
 * Copy rewritten 2026-08 from a marketing brief. The reader is whoever books
 * the crew for live production: a department of one at an athletic department,
 * a regional production company, a house crew lead. Voice is plain and specific
 * on purpose, because that reader can tell in one sentence whether the writer
 * has ever been in a truck.
 *
 * The wedge is the freelancer. Shift schedulers assume a fixed roster on a
 * fixed rota; live production is a different crew on every show, booked per
 * event, most of whom work for someone else next week. Keep that the spine of
 * the argument rather than drifting back into any one vertical.
 *
 * EVERY product claim here was checked against the schema before it shipped.
 * The brief also promised per-position call times, cross-event conflict
 * detection, hours caps for work-study limits, and one-step roster rollover;
 * none of those exist, so none of them are on this page. Do not add a
 * capability claim without confirming it in the code first.
 */

import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { LEGAL_ENTITY, LEGAL_ADDRESS, CONTACT_SUPPORT } from '../config/legal';
import { submitPublicFeedback } from '../api/feedback';
import { getErrorMessage } from '../api/client';
import TurnstileWidget from '../components/auth/TurnstileWidget';
import Logo from '../components/brand/Logo';
import { resolveDashboardPath } from '../utils/dashboardPath';

const CONTACT_INFO = 'info@programrow.com';

// =============================================================================
// Tunable copy constants
// =============================================================================

/** Events per month on the free plan. Must match FREE in config/subscriptions.ts. */
const FREE_EVENTS_PER_MONTH = 4;

/**
 * Set this to a video URL and the hero's primary button becomes "Watch the
 * 2-minute demo", which is what the brief wants leading the page. Until that
 * video exists it walks the reader into How it works instead, because a primary
 * CTA pointing at nothing is worse than no video at all.
 */
const DEMO_VIDEO_URL = '';

// Marketing-side type and rhythm. The dashboards deliberately do NOT use the
// mono face or the display tracking; this is the public site's voice only.
const MONO_LABEL = 'font-mono uppercase';
const SECTION_X = 'px-[clamp(18px,3vw,32px)]';
const CONTAINER = 'max-w-[1180px] mx-auto';
const SECTION_HEADING = 'text-[clamp(30px,4vw,40px)] font-semibold tracking-display text-ink';

// =============================================================================
// Content
// =============================================================================

/** The three failure modes this product exists to end. */
const PROBLEMS = [
  {
    label: 'The spreadsheet',
    body: 'You built it before the season. Then it started. Two people picked up other gigs, one moved markets, and now the only accurate version lives in your head.',
  },
  {
    label: 'The group chat',
    body: 'Call time moved thirty minutes. You posted it at eleven at night. Three people saw it. One of them was already on the bus.',
  },
  {
    label: 'The no-show',
    body: 'Not malice. A better-paying call landed on the same night. But you are forty minutes to air and you are short a camera, and you are the one ringing round.',
  },
];

const STEPS = [
  {
    number: '01',
    accent: 'program' as const,
    title: 'Post the event',
    body: 'Positions, crew call, venue, and the notes your crew actually need. Build it from an event type so the same show is the same positions every time.',
  },
  {
    number: '02',
    accent: 'program' as const,
    title: 'Crew responds',
    body: 'One tap on their phone, show by show. Anyone who has not answered gets chased automatically on a cadence you set, so you are not sending "did you see this" texts at eleven at night.',
  },
  {
    number: '03',
    accent: 'preview' as const,
    title: 'You assign',
    body: 'Fill each position from the people who said yes. The event stays invisible to your crew until you publish it, so you can move things around without anyone being notified.',
  },
];

/**
 * Every right-hand cell is a real field or screen. The brief's original version
 * promised a different call time per position; the schema has one crew call per
 * event (Event.callTime / startTime / endTime) and positions carry duties and a
 * running order, not their own clocks. Corrected rather than dropped, because
 * the contrast it draws is the right one.
 */
const COMPARISON = [
  {
    them: '4:00 PM to 8:00 PM',
    us: 'Crew call 4:30, doors 6:00, live 7:00, with the running order on the event',
  },
  {
    them: 'A name on a list',
    us: 'Position, TOC contact, engineering notes, show timeline, and the documents for that show',
  },
  {
    them: 'A standing availability grid',
    us: 'A yes or no on this specific show',
  },
  {
    them: 'Staff who are there every week',
    us: 'Freelancers with one account across every outfit they work for',
  },
  {
    them: 'An email blast',
    us: 'A draft your crew cannot see, one notification when you publish, and a list of who has not replied',
  },
];

/** Admin side. Each line maps to a screen that exists today. */
const FOR_YOU = [
  'Build an event from an event type, or start from scratch',
  'See who has responded, who declined, and who is ignoring you',
  'Keep an event in draft, invisible to crew, until you are ready',
  'Automatic reminders chase the people who have not answered',
  'Response-rate and utilisation reports show who actually turns up',
  'Hours, pay periods, and payroll exports when the season is over',
];

/**
 * Crew side. The calendar-subscription and offline lines were not in the brief;
 * the writer did not know they existed, and for somebody juggling calls from
 * three different employers they are two of the strongest things on this page.
 */
const FOR_CREW = [
  'Say yes or no to a single show in one tap, from their phone',
  'Every assignment with position, crew call, venue, and the event notes',
  'Subscribe the schedule to Google, Apple, or Outlook once and forget about it',
  'Install it to the home screen, where the schedule still opens with no signal',
  'A reminder before the event, on the timing they pick',
  // SMS is Enterprise-only AND requires the crew member to verify their own
  // handset. This wording is deliberate and is part of the registered A2P
  // consent story. Do not shorten it to "we text your crew".
  'Optional text alerts, on plans that include them, if they turn them on themselves',
];

const PLANS = [
  {
    name: 'Free',
    price: '$0',
    cadence: '/month',
    highlight: false,
    railColor: '#D4D4D2',
    features: [
      `Up to ${FREE_EVENTS_PER_MONTH} events per month`,
      'Unlimited crew members',
      'Email notifications',
      'Reports & CSV exports',
    ],
  },
  {
    name: 'Pro',
    price: '$15',
    cadence: '/month',
    highlight: true,
    railColor: '#E5342A',
    features: ['Up to 20 events per month', 'Unlimited crew members', 'Everything in Free'],
  },
  {
    name: 'Enterprise',
    price: '$25',
    cadence: '/month',
    highlight: false,
    railColor: '#D4D4D2',
    features: ['Unlimited events', 'Unlimited crew members', 'Text alerts: 300 a month included', 'Everything in Pro'],
  },
  {
    name: 'Season Pass',
    price: '$200',
    cadence: ' one-time',
    highlight: false,
    railColor: '#2FBF6B',
    features: ['Unlimited events for 10 months', 'Text alerts: 300 a month included', 'Covers a full August to May season', 'Never auto-renews'],
  },
];

// =============================================================================
// Hero dashboard
// =============================================================================

/**
 * A facsimile of the admin dashboard, drawn rather than screenshotted.
 *
 * A real PNG would be a stable URL whose content goes stale the moment the app
 * changes (exactly the trap the PWA icon fell into), it would blur on a retina
 * display, and it could not reflow on a phone. Built from the same tokens as
 * the real page, this cannot drift from the product's actual look, and the
 * numbers are illustrative rather than a claim about anyone's account.
 */
const HERO_EVENTS = [
  { name: 'Volleyball vs Duquesne', when: 'Fri 12 Sep', crew: '6 of 8 assigned', state: 'short' as const, tag: 'Published' },
  { name: 'Council meeting, live', when: 'Tue 16 Sep', crew: '4 awaiting response', state: 'waiting' as const, tag: 'Published' },
  { name: 'Football vs Wagner', when: 'Sat 20 Sep', crew: 'All confirmed', state: 'ok' as const, tag: 'Published' },
];

const HERO_STATS = [
  { value: '14', label: 'Upcoming events' },
  { value: '38', label: 'Confirmed crew' },
  { value: '2', label: 'Short of crew' },
];

function DashboardPanel() {
  const rail = { short: 'border-l-program', waiting: 'border-l-maybe', ok: 'border-l-preview' };
  const tone = { short: 'text-program-dark', waiting: 'text-maybe-dark', ok: 'text-preview-text' };

  return (
    <div
      aria-hidden="true"
      className="bg-paper border border-rack-hairline min-w-0 lg:-mb-14 overflow-hidden"
    >
      {/* App chrome, so it reads as a screen rather than a diagram */}
      <div className="bg-rack-deep flex items-center justify-between gap-3 px-4 py-3">
        <Logo size={11} on="dark" wordmarkSize={14} />
        <span className={`${MONO_LABEL} flex items-center gap-2 text-[10px] tracking-[0.12em] text-ink-secondary`}>
          <span className="w-1.5 h-1.5 bg-preview inline-block" />
          Live
        </span>
      </div>

      <div className="p-4 flex flex-col gap-4">
        <div className="flex items-baseline justify-between gap-3 border-b border-hairline pb-2.5">
          <span className="text-[15px] font-semibold text-ink">Dashboard</span>
          <span className="text-[11px] text-ink-muted">Next 3 weeks</span>
        </div>

        <div className="grid grid-cols-3 gap-2.5">
          {HERO_STATS.map((stat) => (
            <div key={stat.label} className="bg-white border border-hairline border-t-[3px] border-t-rack px-3 py-2.5">
              <p className="text-[26px] leading-none font-semibold text-ink">{stat.value}</p>
              <p className="text-[10px] text-ink-muted mt-1.5 leading-tight">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-hairline">
          {HERO_EVENTS.map((e) => (
            <div
              key={e.name}
              className={`border-l-[3px] ${rail[e.state]} border-b border-b-hairline-light last:border-b-0 px-3.5 py-2.5`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] font-semibold text-ink truncate">{e.name}</span>
                <span className="text-[10px] text-ink-muted whitespace-nowrap">{e.when}</span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-1">
                <span className={`text-[11px] ${tone[e.state]}`}>{e.crew}</span>
                <span className="text-[9px] px-1.5 py-0.5 border border-preview text-preview-text whitespace-nowrap">
                  {e.tag}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Hero switcher panel
// =============================================================================

/**
 * The logo's own metaphor at full size: a program row of confirmed positions
 * over a preview row of who is still available. It sits beside "here's who to
 * call" because that AVAILABLE row is exactly that. Decorative, so it is hidden
 * from assistive tech rather than read out as fake crew names.
 */
const CONFIRMED_POSITIONS = [
  { role: 'TD', name: 'M. Alvarez' },
  { role: 'CAM 1', name: 'J. Okafor' },
  { role: 'A1', name: 'R. Petrov' },
  { role: 'EVS', name: 'S. Whitcomb' },
];

const AVAILABLE_POSITIONS = [
  { role: 'CAM 2', name: 'D. Lindqvist', filled: true },
  { role: 'CAM 2', name: 'P. Nakamura', filled: true },
  { role: 'GFX', name: 'T. Boone', filled: true },
  { role: 'UTIL', name: 'Unfilled', filled: false },
];

function SwitcherPanel() {
  return (
    <div
      aria-hidden="true"
      className="bg-rack-deep border border-rack-hairline p-[22px] flex flex-col gap-5 min-w-0"
    >
      <div
        className={`${MONO_LABEL} flex items-center justify-between text-[11px] tracking-[0.12em] text-ink-secondary`}
      >
        <span>Fri 12 Sep · Varsity Volleyball</span>
        <span className="text-program">Call 17:00</span>
      </div>

      <div className="flex flex-col gap-[9px]">
        <div className={`${MONO_LABEL} flex items-center gap-2.5 text-[10px] tracking-[0.14em] text-program`}>
          <span className="w-[7px] h-[7px] bg-program" />
          Confirmed
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-[9px]">
          {CONFIRMED_POSITIONS.map((p) => (
            <div
              key={p.role + p.name}
              className="bg-rack-raised border-t-[3px] border-program px-2.5 pt-3 pb-3.5 flex flex-col gap-[7px]"
            >
              <span className={`${MONO_LABEL} text-[10px] tracking-[0.1em] text-ink-secondary`}>{p.role}</span>
              <span className="text-[13px] font-medium text-white">{p.name}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-[9px]">
        <div className={`${MONO_LABEL} flex items-center gap-2.5 text-[10px] tracking-[0.14em] text-preview`}>
          <span className="w-[7px] h-[7px] bg-preview" />
          Available
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-[9px]">
          {AVAILABLE_POSITIONS.map((p, i) => (
            <div
              key={`${p.role}-${i}`}
              className="bg-[#16161A] px-2.5 pt-3 pb-3.5 flex flex-col gap-[7px]"
              style={{ borderTop: p.filled ? '3px solid #2FBF6B' : '3px dashed #4A4A50' }}
            >
              <span className={`${MONO_LABEL} text-[10px] tracking-[0.1em] text-ink-secondary`}>{p.role}</span>
              <span className={`text-[13px] font-medium ${p.filled ? 'text-onDark-body' : 'text-ink-secondary'}`}>
                {p.name}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div
        className={`${MONO_LABEL} border-t border-rack-hairline pt-3.5 flex justify-between text-[11px] text-ink-secondary`}
      >
        <span>7 of 8 positions filled</span>
        <span className="text-onDark-body">Notify crew →</span>
      </div>
    </div>
  );
}

// =============================================================================
// Public contact form (WS8-E) for people evaluating ProgramRow without an
// account. Posts to /api/feedback/public (Turnstile-protected server-side).
// =============================================================================

function ContactForm() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const turnstileConfigured = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (turnstileConfigured && !turnstileToken) {
      toast.error('Please complete the verification challenge.');
      return;
    }
    setSending(true);
    try {
      await submitPublicFeedback({
        category: 'QUESTION',
        message: message.trim(),
        email: email.trim(),
        turnstileToken: turnstileToken ?? undefined,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      });
      setSent(true);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-white border border-hairline-light border-l-[3px] border-l-preview p-6">
        <p className="text-ink font-medium">Thanks, your message is on its way.</p>
        <p className="text-sm text-ink-body mt-1">We'll reply to {email}.</p>
      </div>
    );
  }

  const fieldClasses =
    'block w-full px-3 py-3 border border-hairline bg-white text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack';

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-surface-panel border border-hairline-light p-[clamp(22px,3vw,32px)] flex flex-col gap-[18px]"
    >
      <h3 className="text-lg font-semibold text-ink">Or send us a message right here</h3>
      <div>
        <label htmlFor="contact-email" className="block text-[13px] font-medium text-ink-body mb-[7px]">
          Your email
        </label>
        <input
          id="contact-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={fieldClasses}
          placeholder="you@yourcompany.com"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-[13px] font-medium text-ink-body mb-[7px]">
          Message
        </label>
        <textarea
          id="contact-message"
          required
          minLength={5}
          maxLength={5000}
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={`${fieldClasses} resize-y`}
          placeholder="Tell us about your season, or ask us anything."
        />
      </div>
      <TurnstileWidget onToken={setTurnstileToken} />
      <button
        type="submit"
        disabled={sending || message.trim().length < 5 || !email}
        className="w-full py-3.5 text-sm font-semibold text-white bg-rack hover:bg-program disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-rack transition-colors"
      >
        {sending ? 'Sending…' : 'Send message'}
      </button>
    </form>
  );
}

// =============================================================================
// Landing Page Component
// =============================================================================

export default function LandingPage() {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const { currentOrg, isLoading: orgLoading } = useOrganization();

  // Shared with the `/dashboard` route that email links point at, so the two
  // can never disagree about where a given reader's dashboard is.
  const getDashboardPath = () => resolveDashboardPath(user, currentOrg);

  const isLoading = authLoading || (isAuthenticated && orgLoading);

  const redButton =
    'inline-flex items-center justify-center px-7 py-[15px] text-[15px] font-semibold text-white bg-program hover:bg-program-dark transition-colors';
  const outlineButton =
    'inline-flex items-center justify-center px-7 py-[15px] text-[15px] font-semibold text-white border border-rack-unlit hover:border-white transition-colors';

  return (
    <div className="min-h-screen bg-paper">
      {/* Top rail, the brand's first mark on the page */}
      <div className="h-1 bg-program" />

      {/* Navigation */}
      <nav className={`bg-rack sticky top-0 z-20 border-b border-rack-hairline ${SECTION_X}`}>
        <div className={`${CONTAINER} h-[68px] flex flex-wrap items-center justify-between gap-5`}>
          <Link to="/" aria-label="ProgramRow home" className="flex items-center">
            <Logo size={16} on="dark" wordmarkSize={19} />
          </Link>

          <div
            className={`${MONO_LABEL} hidden md:flex items-center gap-7 text-xs tracking-label text-onDark-secondary`}
          >
            <a href="#how-it-works" className="hover:text-white transition-colors">How it works</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <Link to="/docs" className="hover:text-white transition-colors">Docs</Link>
            <a href="#contact" className="hover:text-white transition-colors">Contact</a>
          </div>

          <div className="flex items-center gap-[18px]">
            {isLoading ? (
              <div className="w-24 h-9 bg-rack-raised animate-pulse" />
            ) : isAuthenticated ? (
              <Link
                to={getDashboardPath()}
                className="inline-flex items-center px-[18px] py-2.5 text-sm font-semibold text-white bg-program hover:bg-program-dark transition-colors"
              >
                Go to Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium text-onDark-body hover:text-white transition-colors">
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center px-[18px] py-2.5 text-sm font-semibold text-white bg-program hover:bg-program-dark transition-colors"
                >
                  Start free
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className={`bg-rack text-white pt-[84px] ${SECTION_X}`}>
        <div className={`${CONTAINER} grid grid-cols-1 lg:grid-cols-2 gap-16 items-start`}>
          <div className="flex flex-col gap-[26px] pb-[88px]">
            <div className={`${MONO_LABEL} flex items-center gap-2.5 text-xs tracking-label-wide text-ink-muted`}>
              <span className="w-2 h-2 bg-program inline-block" />
              Crew scheduling for live production
            </div>
            {/* Each sentence is nowrap so the line can only break BETWEEN
                sentences. Without it the headline reads "Hundreds of events. Never /
                enough crew.", which breaks the rhythm of the setup. */}
            <h1 className="text-[clamp(34px,4.6vw,54px)] leading-[1.05] font-semibold tracking-[-0.035em]">
              <span className="whitespace-nowrap">Hundreds of events.</span>{' '}
              <span className="whitespace-nowrap">Never enough crew.</span>
              <span className="block text-ink-muted">One schedule that actually works.</span>
            </h1>
            <p className="text-lg leading-relaxed text-onDark-body max-w-[540px]">
              Post the schedule, your crew say what they can work, and you assign from who
              actually raised their hand.
            </p>
            <div className="flex flex-wrap gap-3.5 mt-1.5">
              {isLoading ? (
                <div className="w-40 h-12 bg-rack-raised animate-pulse" />
              ) : isAuthenticated ? (
                <Link to={getDashboardPath()} className={redButton}>
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  {DEMO_VIDEO_URL ? (
                    <a href={DEMO_VIDEO_URL} className={redButton}>
                      Watch the 2-minute demo
                    </a>
                  ) : (
                    <a href="#how-it-works" className={redButton}>
                      See how it works
                    </a>
                  )}
                  <Link to="/signup" className={outlineButton}>
                    Start free
                  </Link>
                </>
              )}
            </div>
            <div
              className={`${MONO_LABEL} flex flex-wrap gap-x-8 gap-y-2 mt-3.5 text-xs tracking-[0.06em] text-ink-secondary`}
            >
              <span>Free up to {FREE_EVENTS_PER_MONTH} events / mo</span>
              <span>Unlimited crew, every plan</span>
              <span>No card to start</span>
            </div>
          </div>

          <DashboardPanel />
        </div>
      </section>

      {/* The problem */}
      <section className={`bg-paper pt-[132px] pb-24 ${SECTION_X}`}>
        <div className={`${CONTAINER} flex flex-col gap-12`}>
          <div className="border-b border-hairline pb-6">
            <h2 className={SECTION_HEADING}>You know how this goes</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-hairline border border-hairline">
            {PROBLEMS.map((p) => (
              <div key={p.label} className="bg-white px-7 pt-8 pb-9 flex flex-col gap-3">
                <div className={`${MONO_LABEL} text-[11px] tracking-[0.12em] text-ink-muted`}>{p.label}</div>
                <p className="text-[15px] leading-[1.65] text-ink-body">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section
        id="how-it-works"
        className={`bg-white border-y border-hairline py-24 scroll-mt-[68px] ${SECTION_X}`}
      >
        <div className={`${CONTAINER} grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-12 items-start`}>
          <div className="flex flex-col gap-5">
            <h2 className={SECTION_HEADING}>They tell you what they can work. You assign from there.</h2>
            <p className="text-base leading-relaxed text-ink-body">
              Post your events. Your crew respond show by show, from their phone. You assign
              from the people who actually said yes.
            </p>
            <p className="text-base leading-relaxed text-ink-body">
              Nobody gets scheduled off a guess about where they were supposed to be.
            </p>
          </div>
          <div className="flex flex-col">
            {STEPS.map((step, i) => (
              <div
                key={step.number}
                className={`grid grid-cols-[56px_minmax(0,1fr)] gap-6 py-[26px] ${
                  i < STEPS.length - 1 ? 'border-b border-hairline-light' : ''
                }`}
              >
                <span
                  className={`font-mono text-[30px] font-semibold leading-none ${
                    step.accent === 'program' ? 'text-program' : 'text-preview'
                  }`}
                >
                  {step.number}
                </span>
                <div>
                  <h3 className="text-xl font-semibold text-ink mb-1.5">{step.title}</h3>
                  <p className="text-[15px] leading-relaxed text-ink-body">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for a broadcast */}
      <section className={`bg-paper py-24 ${SECTION_X}`}>
        <div className={`${CONTAINER} flex flex-col gap-11`}>
          <div className="flex flex-wrap items-end justify-between gap-6 border-b border-hairline pb-6">
            <h2 className={SECTION_HEADING}>Shift schedulers weren't built for this</h2>
            <p className="text-base text-ink-body max-w-[520px]">
              Most scheduling software assumes a fixed roster on a fixed rota. Live production
              isn't that. Your crew freelance, your call times move, and the person on Camera 3
              last week is on somebody else's show tonight.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-hairline border border-hairline">
            <div
              className={`${MONO_LABEL} bg-surface-panel px-6 py-3 text-[11px] tracking-[0.12em] text-ink-secondary`}
            >
              A shift scheduler gives you
            </div>
            <div className={`${MONO_LABEL} bg-rack px-6 py-3 text-[11px] tracking-[0.12em] text-onDark-secondary`}>
              ProgramRow gives you
            </div>
            {COMPARISON.map((row) => (
              <Fragment key={row.them}>
                <div className="bg-white px-6 py-5 text-[15px] text-ink-muted">{row.them}</div>
                <div className="bg-white px-6 py-5 text-[15px] text-ink border-l-[3px] border-l-program">
                  {row.us}
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className={`bg-white border-y border-hairline py-24 ${SECTION_X}`}>
        <div className={`${CONTAINER} grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-12 items-center`}>
          <div className="flex flex-col gap-5">
            <h2 className={SECTION_HEADING}>Two events short of a crew. Here's who to call.</h2>
            <p className="text-base leading-relaxed text-ink-body">
              The hard part was never the calendar. It is getting fifty freelancers to answer
              a question.
            </p>
            <p className="text-base leading-relaxed text-ink-body">
              ProgramRow chases the ones who haven't responded, and shows you every upcoming
              event that is still short, while there is still time to do something about it.
            </p>
          </div>
          <SwitcherPanel />
        </div>
      </section>

      {/* Both sides of the call sheet */}
      <section className={`bg-paper py-24 ${SECTION_X}`}>
        <div className={`${CONTAINER} flex flex-col gap-11`}>
          <div className="border-b border-hairline pb-6">
            <h2 className={SECTION_HEADING}>Both sides of the call sheet</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-hairline border border-hairline">
            <div className="bg-white px-7 pt-8 pb-9 flex flex-col gap-4">
              <div className={`${MONO_LABEL} flex items-center gap-2.5 text-[11px] tracking-[0.12em] text-ink-muted`}>
                <span className="w-[7px] h-[7px] bg-program" />
                For you
              </div>
              <ul className="flex flex-col gap-3 text-[15px] leading-relaxed text-ink-body">
                {FOR_YOU.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white px-7 pt-8 pb-9 flex flex-col gap-4">
              <div className={`${MONO_LABEL} flex items-center gap-2.5 text-[11px] tracking-[0.12em] text-ink-muted`}>
                <span className="w-[7px] h-[7px] bg-preview" />
                For your crew
              </div>
              <ul className="flex flex-col gap-3 text-[15px] leading-relaxed text-ink-body">
                {FOR_CREW.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Built for a season */}
      <section className={`bg-white border-y border-hairline py-24 ${SECTION_X}`}>
        <div className={`${CONTAINER} grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-8 items-start`}>
          <h2 className={SECTION_HEADING}>A hundred events, not one shoot</h2>
          <div className="flex flex-col gap-5">
            <p className="text-base leading-relaxed text-ink-body">
              Your volleyball show has the same eight positions every time. So does your Tuesday
               council meeting. Build each one once as an event type and post the whole run from a
              spreadsheet import.
            </p>
            <p className="text-base leading-relaxed text-ink-body">
              Rosters turn over. New people arrive mid-season, regulars move markets, and the
              student who ran replay all year graduates in May. Bulk-invite the new intake from a
              CSV, and removing someone keeps everything they worked on your reports.
            </p>
            <p className="text-base leading-relaxed text-ink-body">
              Crew who work for more than one outfit get one account, not three. They see one
              schedule; each of you sees only your own shows.
            </p>
          </div>
        </div>
      </section>

      {/* Who built this */}
      <section className={`bg-paper pt-20 ${SECTION_X}`}>
        <div className={`${CONTAINER}`}>
          <div className="max-w-[820px] bg-white border border-hairline border-l-[3px] border-l-rack px-8 py-7">
            <div className={`${MONO_LABEL} text-[11px] tracking-[0.12em] text-ink-muted mb-3`}>Who built this</div>
            <p className="text-[17px] leading-relaxed text-ink">
              ProgramRow was built by a broadcast engineer who crewed 140+ events a year with a
              freelance and student crew. The spreadsheet wasn't working there either.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className={`bg-paper py-24 scroll-mt-[68px] ${SECTION_X}`}>
        <div className={`${CONTAINER} flex flex-col gap-11`}>
          <div className="flex flex-wrap items-end justify-between gap-6 border-b border-hairline pb-6">
            <h2 className={SECTION_HEADING}>Pricing</h2>
            <p className="text-base text-ink-body max-w-[460px]">
              Start free and stay free at {FREE_EVENTS_PER_MONTH} events a month. Every plan
              includes unlimited crew members.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`border p-6 pt-7 flex flex-col gap-5 ${
                  plan.highlight ? 'bg-rack border-rack' : 'bg-white border-hairline'
                }`}
                style={{ borderTop: `3px solid ${plan.railColor}` }}
              >
                <div className="flex flex-col gap-2.5">
                  <span
                    className={`${MONO_LABEL} text-xs tracking-[0.12em] ${
                      plan.highlight ? 'text-program' : 'text-ink-secondary'
                    }`}
                  >
                    {plan.name}
                    {plan.highlight && ' · Most Popular'}
                  </span>
                  <div className="flex items-baseline gap-1">
                    <span
                      className={`text-[clamp(30px,4vw,40px)] font-semibold tracking-display ${
                        plan.highlight ? 'text-white' : 'text-ink'
                      }`}
                    >
                      {plan.price}
                    </span>
                    <span className={`text-sm ${plan.highlight ? 'text-onDark-secondary' : 'text-ink-secondary'}`}>
                      {plan.cadence}
                    </span>
                  </div>
                </div>
                <ul
                  className={`flex flex-col gap-[9px] text-sm flex-1 ${
                    plan.highlight ? 'text-onDark-body' : 'text-ink-body'
                  }`}
                >
                  {plan.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                {isLoading ? (
                  <div className={`w-full h-11 animate-pulse ${plan.highlight ? 'bg-rack-raised' : 'bg-paper'}`} />
                ) : (
                  <Link
                    to={isAuthenticated ? '/admin/settings/billing' : '/signup'}
                    className={`block w-full py-3 text-center text-sm font-semibold transition-colors ${
                      plan.highlight
                        ? 'text-white bg-program hover:bg-program-dark'
                        : 'text-ink border border-rack hover:bg-rack hover:text-white'
                    }`}
                  >
                    {isAuthenticated ? 'Manage Plan' : 'Get Started'}
                  </Link>
                )}
              </div>
            ))}
          </div>
          <p className="text-sm text-ink-secondary max-w-[720px] leading-relaxed">
            All prices are in USD and tax-inclusive. Schools and organizations that
            pay by invoice or purchase order can write to{' '}
            <a href="mailto:billing@programrow.com" className="text-program font-medium hover:text-program-dark">
              billing@programrow.com
            </a>.
          </p>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className={`bg-white border-t border-hairline py-24 scroll-mt-[68px] ${SECTION_X}`}>
        <div className={`${CONTAINER} grid grid-cols-1 lg:grid-cols-2 gap-x-[72px] gap-y-12 items-start`}>
          <div className="flex flex-col gap-7">
            <h2 className={SECTION_HEADING}>Get in touch</h2>
            <p className="text-base leading-relaxed text-ink-body">
              Questions about ProgramRow, getting your crew on board, or anything else? We
              answer every message.
            </p>
            <div className="grid grid-cols-1 gap-px bg-hairline-light border border-hairline-light">
              <div className="bg-white px-6 py-[22px] flex flex-col gap-1.5">
                <h3 className="text-base font-semibold text-ink">Sales &amp; General Inquiries</h3>
                <p className="text-sm text-ink-secondary">New organizations, demos, and general questions.</p>
                <a
                  href={`mailto:${CONTACT_INFO}`}
                  className="font-mono text-[13px] text-program hover:text-program-dark"
                >
                  {CONTACT_INFO}
                </a>
              </div>
              <div className="bg-white px-6 py-[22px] flex flex-col gap-1.5">
                <h3 className="text-base font-semibold text-ink">Support</h3>
                <p className="text-sm text-ink-secondary">Help with an existing account or organization.</p>
                <a
                  href={`mailto:${CONTACT_SUPPORT}`}
                  className="font-mono text-[13px] text-program hover:text-program-dark"
                >
                  {CONTACT_SUPPORT}
                </a>
              </div>
            </div>
            <p className="text-[13px] text-ink-muted">
              ProgramRow is operated by {LEGAL_ENTITY}, {LEGAL_ADDRESS}.
            </p>
          </div>

          <ContactForm />
        </div>
      </section>

      {/* Final CTA */}
      <section className={`bg-paper pb-24 ${SECTION_X}`}>
        <div
          className={`${CONTAINER} bg-rack border-t-[5px] border-program px-[clamp(24px,4vw,56px)] py-[clamp(36px,5vw,64px)] flex flex-wrap items-center justify-between gap-8`}
        >
          <div className="flex flex-col gap-3 max-w-[620px]">
            <h2 className="text-[clamp(28px,3.5vw,36px)] font-semibold tracking-display text-white">
              Season not built yet?
            </h2>
            <p className="text-base text-onDark-secondary">
              It takes about an hour to get your roster in and your first month of events on
              the board.
            </p>
          </div>
          {isLoading ? (
            <div className="w-40 h-12 bg-rack-raised animate-pulse" />
          ) : (
            <Link
              to={isAuthenticated ? getDashboardPath() : '/signup'}
              className="inline-flex items-center px-[30px] py-4 text-[15px] font-semibold text-ink bg-white hover:bg-program hover:text-white transition-colors"
            >
              {isAuthenticated ? 'Go to Dashboard' : 'Start free'}
            </Link>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className={`bg-rack pt-14 pb-10 ${SECTION_X}`}>
        <div className={`${CONTAINER} flex flex-col gap-9`}>
          <div className="flex flex-wrap items-center justify-between gap-6 pb-7 border-b border-rack-hairline">
            <Logo size={14} on="dark" wordmarkSize={17} />
            <div
              className={`${MONO_LABEL} flex flex-wrap gap-x-[22px] gap-y-2 text-xs tracking-[0.06em] text-onDark-secondary`}
            >
              <Link to="/docs" className="hover:text-white transition-colors">Docs</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/sms-opt-in" className="hover:text-white transition-colors">SMS Policy</Link>
              <a href="#contact" className="hover:text-white transition-colors">Contact</a>
              <Link to="/login" className="hover:text-white transition-colors">Sign in</Link>
              <Link to="/signup" className="hover:text-white transition-colors">Start free</Link>
            </div>
          </div>
          <div className="flex flex-col gap-2.5 text-[13px] leading-[1.65] text-ink-muted max-w-[860px]">
            {/* Campus IT objection-handler, deliberately findable rather than
                prominent. Worded to what is actually true: no SIS connection and
                we never ask for class schedules. It stops short of promising
                that nothing school-related can ever appear, because availability
                notes are free text the crew member types themselves. */}
            <p className="text-xs text-ink-secondary">
              Students say when they are available. ProgramRow does not connect to your SIS
              and never asks for class schedules or grades.
            </p>
            <p>
              ProgramRow is operated by {LEGAL_ENTITY}, {LEGAL_ADDRESS} &middot;{' '}
              <a
                href={`mailto:${CONTACT_SUPPORT}`}
                className="text-onDark-secondary hover:text-white transition-colors"
              >
                {CONTACT_SUPPORT}
              </a>
            </p>
            {/* Registered A2P 10DLC disclosure. This exact wording is part of the
                approved campaign's evidence. Do not reword or remove it. */}
            <p className="text-xs text-ink-secondary">
              Text-message notifications are optional and sent only to crew members
              who individually opt in. Message frequency varies; message and data
              rates may apply. Reply STOP to unsubscribe or HELP for help. See our{' '}
              <Link to="/privacy" className="underline text-onDark-secondary hover:text-white">Privacy Policy</Link>
              {' '}and{' '}
              <Link to="/terms" className="underline text-onDark-secondary hover:text-white">Terms of Service</Link>.
            </p>
            <p className="text-xs text-ink-secondary">
              &copy; {new Date().getFullYear()} ProgramRow. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
